import { Box, Divider, useMediaQuery, useTheme, Zoom } from "@mui/material";
import moment from "moment";
import "moment-timezone";
import { useRef, useState } from "react";
import "react-multi-carousel/lib/styles.css";
import { MyMealsQuantitySelectionMode } from "../../Account/Subscriptions/subscriptionsUtils/subscriptions-types";
import { GroupBy, SubscriptionStatus } from "../../OrderFlow/utils/orderFlowUtils";
import { ORDER_STATUS } from "../../Shared/utils/constants";
import "../utils/myMealsCSS.css";
import { CartButton } from "./CartButton/CartButton";
import { MealDaySection_Carousel } from "./MealDaySection_Carousel";
import { MealDaySection_InfoSection } from "./MealDaySection_InfoSection";
import { MealDaySection_OrderSelector } from "./MealDaySection_OrderSelector";
import { SkipDayDialog } from "./SkipDayDialog";

export default function MealDaySection(props) {
  const [isSkipDayDialogOpen, setIsSkipDayDialogOpen] = useState(false);

  const sortedOrders = Object.fromEntries(
    Object.entries(GroupBy("SubscriptionLinkId", props.orders)).map((orders) => {
      orders[1].sort((order1, order2) => {
        if (order1.StartDate < order2.StartDate) {
          return -1;
        } else if (order1.StartDate > order2.StartDate) {
          return 1;
        } else {
          return 0;
        }
      });

      return orders;
    })
  );

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"), { noSsr: true });
  const isTablet = useMediaQuery(theme.breakpoints.down("md"), { noSsr: true });

  const NUM_DAYS_MOBILE = 2;
  const NUM_DAYS_TABLET = 3;
  const NUM_DAYS_DESKTOP = 5;
  const numDaysPerSlide = isMobile ? NUM_DAYS_MOBILE : isTablet ? NUM_DAYS_TABLET : NUM_DAYS_DESKTOP;

  const currentSubLinkId = props.selectedOrder.SubscriptionLinkId;

  const currentOrderSet = sortedOrders[currentSubLinkId];

  const currentOrderPos = currentOrderSet.indexOf(props.selectedOrder);

  const handleNextOrder = () => {
    handleFutureOrder(
      currentOrderSet[currentOrderPos].OrderNumber,
      currentOrderSet[currentOrderPos].SubscriptionLinkId,
      props.days[0].DateFormatted,
      props.days[props.days.length - 1].DateFormatted,
      false,
      true
    );
  };

  const handlePreviousOrder = () => {
    handleFutureOrder(
      currentOrderSet[currentOrderPos].OrderNumber,
      currentOrderSet[currentOrderPos].SubscriptionLinkId,
      props.days[0].DateFormatted,
      props.days[props.days.length - 1].DateFormatted,
      true,
      false
    );
  };

  const handleFutureOrder = (orderNum, subLinkId, start, end, previous, future) => {
    props.getFutureOrder(orderNum, subLinkId, start, end, previous, future);
  };

  const mealDayClickScrollToTarget = useRef(null);

  const onMealDayClick = (day) => {
    // Handle the day change
    props.handleDayChange(day);

    // Delay to allow DOM updates before scrolling
    setTimeout(() => {
      if (mealDayClickScrollToTarget.current) {
        // Get the height of the sticky bar
        const stickyBarHeight = document.getElementById("mealControlBar").offsetHeight;

        // Calculate the scroll position (taking the sticky header into account)
        const scrollPosition =
          mealDayClickScrollToTarget.current.getBoundingClientRect().top + window.pageYOffset - stickyBarHeight;

        // Smooth scroll to the calculated position
        window.scrollTo({ top: scrollPosition, behavior: "smooth" });
      }
    }, 0); // A small delay allows DOM updates before scrolling
  };

  const handleSkipDayClick = () => {
    setIsSkipDayDialogOpen(true);
  };

  const handleSkipDayDialogClose = () => {
    setIsSkipDayDialogOpen(false);
  };

  const handleSkipDayDialogConfirm = () => {
    props.handleSkipDay();
    setIsSkipDayDialogOpen(false);
  };

  const calculateCutOffDay = () => {
    const date = props.selectedDay && moment.utc(props.selectedDay.CutOffDate).local();

    if (!date) {
      return "Unknown";
    } else {
      const dateUTC = moment.utc(props.selectedDay.CutOffDate);
      const dateUTCOffsetAdjusted = dateUTC.add(date.utcOffset() / 60, "hours");

      return dateUTCOffsetAdjusted.format("dddd, MMMM Do YYYY h A") + " " + moment.tz(moment.tz.guess()).format("z");
    }
  };

  let hasQuantityGreaterThanZero = (items, quantityProperty) => {
    return items && items.reduce((sum, item) => sum + item[quantityProperty], 0) > 0;
  };

  let showSkipDayButton =
    props.selectedDay.Config.CanRemoveMeals &&
    props.selectedDay.Config.CanRemoveProducts &&
    (hasQuantityGreaterThanZero(props.selectedDay.Meals, "Quantity") ||
      hasQuantityGreaterThanZero(props.selectedDay.Products, "PurchasedQuantity"));

  let showDeliveryDayText = props.companyText.find((text) => text.Title === "DeliveryDayText").Text !== "";

  let {
    myMealsConfig: { MealQuantitySelectionMode, RestrictAddRemoveDays, FutureOrderInfoText, ShowDayIcons },
  } = props;

  const restrictMealQuantitySelection = MealQuantitySelectionMode === MyMealsQuantitySelectionMode.NoChanges;
  const orderHasFutureStatus = props.selectedOrder.OrderStatus === ORDER_STATUS.FUTURE;

  const futureOrderWithNoneSubStatus =
    orderHasFutureStatus && props.selectedOrder.SubscriptionStatus === SubscriptionStatus.None;

  const cutoffDay = calculateCutOffDay();

  const cutoffDayText = props?.companyText?.find((text) => text?.Title === "CutOffDayText")?.Text;
  const deliveryDayText = props?.companyText?.find((text) => text?.Title === "DeliveryDayText")?.Text;

  const deliveryDay = props.selectedDay && moment(props.selectedDay.ShipDate).format("dddd, MMMM Do YYYY");

  return (
    <>
      <Box className="flex-parent centered" mb={1}>
        {/* ORDER SELECTION */}
        <MealDaySection_OrderSelector
          isFutureOrder={props.isFutureOrder}
          currentOrderPos={currentOrderPos}
          currentOrderSet={currentOrderSet}
          handlePreviousOrder={handlePreviousOrder}
          handleNextOrder={handleNextOrder}
          selectedDay={props.selectedDay}
          days={props.days}
          allowFutureOrderCreation={props.allowFutureOrderCreation}
          isSubscriptionOrder={props.isSubscriptionOrder}
          futureOrderWithNoneSubStatus={futureOrderWithNoneSubStatus}
        />
      </Box>

      <Box id="mealControlBar" bgcolor="inherit" p={0}>
        {/* MEAL DAY CAROUSEL */}
        <MealDaySection_Carousel
          days={props.days}
          selectedDay={props.selectedDay}
          skippedDays={props.skippedDays}
          selectedFutureOrder={props.selectedFutureOrder}
          companyText={props.companyText}
          myMealsConfig={props.myMealsConfig}
          onMealDayClick={onMealDayClick}
          numDaysPerSlide={numDaysPerSlide}
          showDayIcons={ShowDayIcons}
          mealQuantitySelectionMode={MealQuantitySelectionMode}
          restrictAddRemoveDays={RestrictAddRemoveDays}
          orderHasFutureStatus={orderHasFutureStatus}
        />

        {!props.isTabletOrMobile && (
          <Zoom
            // Show divider only when the user has scrolled past the top of the page.
            in={props.scrollPosition > 0}
          >
            <Divider
              sx={{
                marginY: theme.spacing(0.5),
                borderBottomWidth: "medium",
                borderColor: theme.palette.primary.main,
              }}
            />
          </Zoom>
        )}
      </Box>

      {!props.allDaysPastCutoff && props.days && (
        <CartButton
          cartIcon={props.cartIcon}
          checkSubscriptionMealsPrehook={props.checkSubscriptionMealsPrehook}
          isUpdatingPrice={props.isUpdatingPrice}
          quickSaveToolTipText={props.quickSaveToolTipText}
          saveAndUpdateCart={props.saveAndUpdateCart}
          saveAndUpdateFutureOrder={props.saveAndUpdateFutureOrder}
          saveAndUpdateText={props.saveAndUpdateText}
          selectedFutureOrder={props.selectedFutureOrder}
          selectedMealQuantity={props.selectedMealQuantity}
          selectedOrder={props.selectedOrder}
          showQuickSaveButton={props.showQuickSaveButton}
          subtotal={props.subtotal}
          toggleDrawer={props.toggleDrawer}
          totalMealQuantity={props.totalMealQuantity}
        />
      )}

      {/* INFO SECTION */}
      <Box ref={mealDayClickScrollToTarget}>
        <MealDaySection_InfoSection
          isFutureOrder={props.isFutureOrder}
          cutoffDayText={cutoffDayText}
          cutoffDay={cutoffDay}
          futureOrderInfoText={FutureOrderInfoText}
          showDeliveryDayText={showDeliveryDayText}
          deliveryDayText={deliveryDayText}
          deliveryDay={deliveryDay}
          showSkipDayButton={showSkipDayButton}
          restrictMealQuantitySelection={restrictMealQuantitySelection}
          restrictAddRemoveDays={RestrictAddRemoveDays}
          handleSkipDayClick={handleSkipDayClick}
          allDaysPastCutoff={props.allDaysPastCutoff}
        />
      </Box>

      {
        // By conditionally displaying this modal, we prevent the modal from being rendered on page load,
        // which then prevents the modal logic from being executed until the modal is actually shown.
        isSkipDayDialogOpen && (
          <SkipDayDialog
            isSkipDayDialogOpen={isSkipDayDialogOpen}
            handleSkipDayDialogClose={handleSkipDayDialogClose}
            handleSkipDayDialogConfirm={handleSkipDayDialogConfirm}
            skipDayModalTitle={props.companyText.find((text) => text.Title === "SkipDayModalTitle").Text}
            skipDayModalDescription={props.companyText.find((text) => text.Title === "SkipDayModalDescription").Text}
          />
        )
      }
    </>
  );
}
